import axios from 'axios';

import {
  KEY_ACCESS_TOKEN,
  getItem,
  logoutUser,
  setItem,
} from './localStorageManager';

// In production the API lives on its own host; a relative "/api/" only works behind
// the Vite dev proxy. Fall back to the production API if the env var is not set.
const baseURL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? 'https://api.surpassin.com/api' : '/api/');

const REFRESH_ENDPOINT = '/loginService/v1/refreshToken';
const AUTH_BYPASS_PATHS = [
  '/loginService/v1/authenticate',
  '/loginService/v1/authenticateSuperAdmin',
  '/loginService/v1/socialLogin',
  REFRESH_ENDPOINT,
  '/loginService/v1/logout',
];

const withBearer = (config) => {
  const token = getItem(KEY_ACCESS_TOKEN);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// withCredentials is required: the backend issues the refresh token as an
// HttpOnly cookie on login and reads it back on /refreshToken.
export const axiosClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});
axiosClient.interceptors.request.use(withBearer);

const refreshClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});
refreshClient.interceptors.request.use(withBearer);

let refreshRequest = null;

const shouldBypassRefresh = (url = '') =>
  AUTH_BYPASS_PATHS.some((path) => url.includes(path));

const handleAuthFailure = () => {
  logoutUser();
  window.location.href = '/auth/login';
};

const attemptRefresh = async (originalRequest) => {
  const refreshCall = refreshRequest ?? refreshClient.get(REFRESH_ENDPOINT);
  refreshRequest = refreshCall;
  try {
    const refreshResponse = await refreshCall;
    const isSuccessStatus =
      refreshResponse?.status >= 200 && refreshResponse?.status < 300;
    const hasExplicitCode = typeof refreshResponse?.data?.code === 'number';
    const isSuccessCode = !hasExplicitCode || refreshResponse?.data?.code === 1;
    const newJwtToken = refreshResponse?.data?.data?.[0]?.jwttoken;

    if (newJwtToken) {
      setItem(KEY_ACCESS_TOKEN, newJwtToken);
    }
    if (isSuccessStatus && isSuccessCode) {
      if (newJwtToken) {
        originalRequest.headers.Authorization = `Bearer ${newJwtToken}`;
      }
      return axiosClient(originalRequest);
    }
    throw new Error('Unable to refresh session');
  } catch (refreshError) {
    handleAuthFailure();
    return Promise.reject(refreshError);
  } finally {
    if (refreshRequest === refreshCall) {
      refreshRequest = null;
    }
  }
};

axiosClient.interceptors.response.use(
  async (response) => {
    // Some backend endpoints signal auth expiry inside a 200 body.
    const originalRequest = response.config ?? {};
    if (
      response?.data?.status === 401 &&
      !originalRequest._retry &&
      !shouldBypassRefresh(originalRequest.url)
    ) {
      originalRequest._retry = true;
      return attemptRefresh(originalRequest);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config ?? {};
    const status = error.response?.status;

    // Network / CORS / timeout — not a session expiry; surface the error.
    if (!error.response) {
      return Promise.reject(error);
    }

    // Authorisation failure (wrong role), not authentication — stay logged in.
    if (status === 403) {
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      !originalRequest._retry &&
      !shouldBypassRefresh(originalRequest.url)
    ) {
      originalRequest._retry = true;
      return attemptRefresh(originalRequest);
    }

    if (status === 401) {
      handleAuthFailure();
    }

    return Promise.reject(error);
  }
);
